import { Trans } from "@lingui/react/macro";
import { useCallback, useEffect, useState } from "react";

import { errorText } from "../../../app/error-text";
import { maybeReportError } from "../../../app/errors";
import { Button } from "../../../components/ui-kit/button";
import { Spinner } from "../../../components/ui-kit/spinner";
import {
  loadPermissionMetadata,
  type PermissionMetadata,
} from "./permissions-utils";

/**
 * The permission bits with their implication / exclusion rules, as both the
 * permissions dialog and the new-user wizard need them. `metadata` is null
 * until the fetch lands; the fetch itself is cached for the session by
 * {@link loadPermissionMetadata}.
 *
 * Data loading, so a failure stays in the dialog with a retry rather than
 * reaching the global error dialog and leaving a spinner behind it.
 *
 * `enabled` is for the caller that renders without the metadata at all - a
 * patient's account shows only its feature flags - so it does not pay for a
 * fetch it will never read.
 */
export function usePermissionsMetadata(enabled = true): {
  metadata: PermissionMetadata | null;
  loadError: unknown;
  retry: () => void;
} {
  const [metadata, setMetadata] = useState<PermissionMetadata | null>(null);
  // Kept as the error, not its text: `errorText` is pure, so rendering it late
  // is what follows a locale switch.
  const [loadError, setLoadError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setMetadata(await loadPermissionMetadata());
    } catch (error) {
      maybeReportError(error);
      setLoadError(error);
    }
  }, []);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  return { metadata, loadError, retry: () => void load() };
}

/** What the pickers' grid column shows while the metadata is missing. */
export function PermissionsMetadataFallback(props: {
  loadError: unknown;
  retry: () => void;
  className?: string;
}) {
  if (props.loadError) {
    return (
      <div
        className={`flex min-h-32 flex-col items-center justify-center gap-3 ${props.className ?? ""}`}
      >
        <p className="text-destructive text-sm">{errorText(props.loadError)}</p>
        <Button type="button" variant="subtle" onClick={props.retry}>
          <Trans>Try again</Trans>
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-32 items-center justify-center ${props.className ?? ""}`}
    >
      <Spinner />
    </div>
  );
}
